import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { DiscoveryCursor, DiscoveryProfileCard } from '../../services/profileCallables';

export interface DiscoveryState {
  items: DiscoveryProfileCard[];
  total: number | null;
  nextCursor: DiscoveryCursor | null;
  query: string;
  roleTypes: string[];
  tags: string[];
  allTags: string[];
  loading: boolean;
  error: string | null;
}

const initialState: DiscoveryState = {
  items: [],
  total: null,
  nextCursor: null,
  query: '',
  roleTypes: [],
  tags: [],
  allTags: [],
  loading: false,
  error: null,
};

const discoverySlice = createSlice({
  name: 'discovery',
  initialState,
  reducers: {
    discoverySearchRequested(state, action: PayloadAction<{ append?: boolean } | undefined>) {
      if (!action.payload?.append) {
        state.loading = true;
      }
      state.error = null;
    },
    discoverySearchSucceeded(
      state,
      action: PayloadAction<{
        items: DiscoveryProfileCard[];
        total: number | null;
        nextCursor: DiscoveryCursor | null;
        allTags?: string[];
        append?: boolean;
      }>,
    ) {
      state.items = action.payload.append
        ? [...state.items, ...action.payload.items]
        : action.payload.items;
      state.total = action.payload.total;
      state.nextCursor = action.payload.nextCursor;
      if (action.payload.allTags) state.allTags = action.payload.allTags;
      state.loading = false;
    },
    discoveryFailed(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    setDiscoveryQuery(state, action: PayloadAction<string>) {
      state.query = action.payload;
    },
    toggleDiscoveryRoleType(state, action: PayloadAction<string>) {
      const v = action.payload;
      state.roleTypes = state.roleTypes.includes(v)
        ? state.roleTypes.filter((x) => x !== v)
        : [...state.roleTypes, v];
    },
    toggleDiscoveryTag(state, action: PayloadAction<string>) {
      const v = action.payload;
      state.tags = state.tags.includes(v) ? state.tags.filter((x) => x !== v) : [...state.tags, v];
    },
  },
});

export const {
  discoverySearchRequested,
  discoverySearchSucceeded,
  discoveryFailed,
  setDiscoveryQuery,
  toggleDiscoveryRoleType,
  toggleDiscoveryTag,
} = discoverySlice.actions;

export default discoverySlice.reducer;
